import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, AlertCircle, Pencil, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import GanttView from "@/components/GanttView";
import BulkUploadButton from "@/components/BulkUploadButton";

const STATUS_OPTS = [
    { id: "open", label: "Open", color: "#FFB703", bg: "#FFF6E5" },
    { id: "in_progress", label: "In Progress", color: "#002FA7", bg: "#E5EBFF" },
    { id: "done", label: "Done", color: "#2A9D8F", bg: "#E0F4F1" },
];
const PRIORITY = [
    { id: "low", color: "#94a3b8" },
    { id: "medium", color: "#FFB703" },
    { id: "high", color: "#E63946" },
];

const emptyTask = { title: "", description: "", part_id: "", parent_task_id: "", depends_on: [], assigned_to: "", start_date: "", due_date: "", actual_start: "", actual_end: "", status: "open", priority: "medium" };

export default function Tasks() {
    const { user } = useAuth();
    const isExecutor = user?.role === "task_executor";
    const today = new Date().toISOString().slice(0, 10);
    const [searchParams, setSearchParams] = useSearchParams();
    const urlPartId = searchParams.get("part_id") || "";
    const autoOpenNew = searchParams.get("new") === "1";
    const [tasks, setTasks] = useState([]);
    const [parts, setParts] = useState([]);
    const [users, setUsers] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyTask);
    const [editingId, setEditingId] = useState(null);
    const [filterUser, setFilterUser] = useState("all");
    const [filterStatus, setFilterStatus] = useState("all");
    const [filterPart, setFilterPart] = useState(urlPartId || "all");
    const [filterCustomer, setFilterCustomer] = useState("all");
    const [view, setView] = useState("list");

    const load = async () => {
        const [t, p, u] = await Promise.all([
            api.get("/tasks"),
            api.get("/parts"),
            api.get("/users").catch(() => ({ data: [] })),
        ]);
        setTasks(t.data); setParts(p.data); setUsers(u.data || []);
    };
    useEffect(() => { load(); }, []);

    // Auto-open dialog with prefilled part_id when ?new=1 is in URL
    useEffect(() => {
        if (autoOpenNew && urlPartId && !open) {
            setForm({ ...emptyTask, part_id: urlPartId });
            setOpen(true);
            // Clear ?new=1 from URL so it doesn't re-open on edit/refresh
            const next = new URLSearchParams(searchParams);
            next.delete("new");
            setSearchParams(next, { replace: true });
        }
    }, [autoOpenNew, urlPartId]);  // eslint-disable-line react-hooks/exhaustive-deps

    const partById = useMemo(() => Object.fromEntries(parts.map((p) => [p.part_id, p])), [parts]);
    const userById = useMemo(() => Object.fromEntries((users || []).map((u) => [u.user_id, u])), [users]);

    const filtered = useMemo(() => tasks.filter((t) => {
        if (filterPart !== "all" && (t.part_id || "") !== filterPart) return false;
        if (filterCustomer !== "all") {
            const cust = (partById[t.part_id]?.customer_name || "").trim();
            if (cust !== filterCustomer) return false;
        }
        if (filterUser !== "all") {
            const ta = t.assigned_to || "";
            if (filterUser === "unassigned" ? ta !== "" : ta !== filterUser) return false;
        }
        if (filterStatus !== "all" && (t.status || "") !== filterStatus) return false;
        return true;
    }), [tasks, filterPart, filterCustomer, filterUser, filterStatus, partById]);

    const isOverdue = (t) => {
        if (t.status === "done" || !t.due_date) return false;
        if (t.part_id && partById[t.part_id]?.status === "PO") return false;
        return t.due_date < today;
    };

    const save = async () => {
        if (!form.title.trim()) { toast.error("Title required"); return; }
        // Two scenarios:
        // 1. form.part_id is locked (opened from a part link / editing) → use that single id
        // 2. Otherwise the dialog shows the Customer → checkbox-list picker; use _picked_part_ids
        let targetIds = [];
        if (form.part_id) targetIds = [form.part_id];
        else if (Array.isArray(form._picked_part_ids) && form._picked_part_ids.length) targetIds = [...form._picked_part_ids];
        if (targetIds.length === 0) { toast.error("Tick at least one part — every task must belong to a part"); return; }
        const basePayload = { ...form };
        delete basePayload._dlg_customer; delete basePayload._picked_part_ids; delete basePayload._dates_dirty;
        try {
            if (editingId) {
                await api.put(`/tasks/${editingId}`, { ...basePayload, part_id: targetIds[0] });
                toast.success("Task updated");
            } else {
                await Promise.all(targetIds.map((pid) => api.post("/tasks", { ...basePayload, part_id: pid })));
                toast.success(targetIds.length > 1 ? `Created ${targetIds.length} tasks (one per part)` : "Task created");
            }
            setOpen(false); setForm(emptyTask); setEditingId(null);
            await load();
        } catch { toast.error("Save failed"); }
    };
    const startEdit = (t) => {
        setEditingId(t.task_id);
        setForm({ ...emptyTask, ...t, _dates_dirty: false, reschedule_reason: "" });
        setOpen(true);
    };
    const remove = async (id) => {
        if (!window.confirm("Delete this task?")) return;
        await api.delete(`/tasks/${id}`); await load();
    };
    const setStatus = async (t, status) => {
        // task_executor users hit the restricted endpoint that also stamps actual dates.
        if (isExecutor) {
            try { await api.post(`/tasks/${t.task_id}/status`, { status }); await load(); }
            catch (e) { toast.error(e.response?.data?.detail || "Status update denied"); }
            return;
        }
        await api.put(`/tasks/${t.task_id}`, { ...t, status });
        await load();
    };
    const markDoneWithPhoto = async (t, photoPath) => {
        try {
            await api.post(`/tasks/${t.task_id}/status`, { status: "done", completion_photo_path: photoPath || "" });
            toast.success("Marked done");
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };
    const addComment = async (t, text) => {
        try {
            await api.post(`/tasks/${t.task_id}/comment`, { text });
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Comment failed"); }
    };
    const uploadCompletionPhoto = async (t, file) => {
        const fd = new FormData(); fd.append("file", file);
        try {
            const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
            if (r.data?.path) await markDoneWithPhoto(t, r.data.path);
        } catch (e) { toast.error("Photo upload failed"); }
    };
    const updateDates = async (t, { start_date, due_date }) => {
        if (isExecutor) { toast.error("Task executors can't change dates"); return; }
        try {
            // Inline Gantt drag → no reason captured (use the dialog to capture a reason)
            await api.put(`/tasks/${t.task_id}`, { ...t, start_date, due_date, reschedule_reason: "" });
            await load();
        } catch { toast.error("Date update failed"); }
    };

    // Row reorder — within the same part-group only (cross-group reorder would
    // break the part-grouping in the gantt). Supports both main tasks and sub-
    // tasks. Sort_order is rewritten across the affected group.
    const reorderRows = async (srcId, tgtId) => {
        if (isExecutor) return;
        const src = tasks.find((t) => t.task_id === srcId);
        const tgt = tasks.find((t) => t.task_id === tgtId);
        if (!src || !tgt) return;
        if ((src.part_id || "") !== (tgt.part_id || "")) {
            toast.error("Drag within the same part group only — tasks are grouped by part."); return;
        }
        const group = tasks
            .filter((t) => (t.part_id || "") === (src.part_id || ""))
            .sort((a, b) => {
                const pa = a.parent_task_id ? 1 : 0; const pb = b.parent_task_id ? 1 : 0;
                if (pa !== pb) return pa - pb;
                return (a.sort_order ?? 9999) - (b.sort_order ?? 9999);
            });
        const srcIdx = group.findIndex((t) => t.task_id === srcId);
        const tgtIdx = group.findIndex((t) => t.task_id === tgtId);
        if (srcIdx < 0 || tgtIdx < 0) return;
        const reordered = [...group];
        const [moved] = reordered.splice(srcIdx, 1);
        reordered.splice(tgtIdx, 0, moved);
        try {
            await Promise.all(reordered.map((t, i) => api.put(`/tasks/${t.task_id}`, { ...t, sort_order: i, reschedule_reason: "" })));
            toast.success("Row reordered");
            await load();
        } catch { toast.error("Reorder failed"); }
    };

    // Member-wise load
    const loadByUser = useMemo(() => {
        const map = {};
        filtered.forEach((t) => {
            if (t.status === "done") return;
            const u = t.assigned_to || "unassigned";
            if (!map[u]) map[u] = { open: 0, in_progress: 0, overdue: 0 };
            map[u][t.status] = (map[u][t.status] || 0) + 1;
            if (isOverdue(t)) map[u].overdue += 1;
        });
        return map;
    }, [filtered, partById, today]);
    const overdueCount = filtered.filter(isOverdue).length;

    return (
        <div className="space-y-6 p-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold">Tasks</h1>
                    <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider mt-1">Project management · close tasks faster · improve hit rate</p>
                </div>
                <div className="flex items-center gap-2">
                    {overdueCount > 0 && (
                        <div className="px-3 py-2 bg-[#FEE2E2] border border-[#FCA5A5] text-[#7F1D1D] inline-flex items-center gap-2 font-mono text-sm" data-testid="overdue-alert">
                            <AlertCircle size={16} /> {overdueCount} overdue task{overdueCount > 1 ? "s" : ""}
                        </div>
                    )}
                    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(emptyTask); setEditingId(null); } }}>
                        {!isExecutor && (
                            <Button
                                className="rounded-none bg-[#002FA7] hover:bg-[#00227B]"
                                data-testid="new-task-btn"
                                onClick={() => {
                                    // Pre-fill from active filters so users don't have to re-select
                                    const seed = { ...emptyTask };
                                    if (filterCustomer !== "all") seed._dlg_customer = filterCustomer;
                                    if (filterPart !== "all") {
                                        seed.part_id = filterPart;
                                        seed._picked_part_ids = [filterPart];
                                    }
                                    setForm(seed);
                                    setEditingId(null);
                                    setOpen(true);
                                }}
                            >
                                <Plus size={14} className="mr-2" /> New Task
                            </Button>
                        )}
                        {!isExecutor && <BulkUploadButton entity="tasks" onComplete={load} />}
                        <DialogContent className="rounded-none max-w-xl max-h-[85vh] flex flex-col overflow-hidden p-0">
                            <DialogHeader className="px-6 pt-6"><DialogTitle>{editingId ? "Edit task" : "New task"}</DialogTitle></DialogHeader>
                            <div className="overflow-y-auto flex-1 px-6 pb-4" data-testid="task-dialog-body">
                                <div className="grid grid-cols-2 gap-3">
                                <Field label="Title *" full>
                                    <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title-input" />
                                </Field>
                                <Field label="Description" full>
                                    <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                                </Field>
                                <Field label="Linked Part * (project)">
                                    {form.part_id ? (
                                        <div className="border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm font-mono flex items-center justify-between" data-testid="task-part-locked">
                                            <span>{partById[form.part_id]?.part_name || form.part_id} {partById[form.part_id]?.part_number ? `(${partById[form.part_id].part_number})` : ""}</span>
                                            {!editingId && (
                                                <button type="button" className="text-[10px] uppercase tracking-wider text-zinc-400 hover:text-[#E63946]" onClick={() => setForm({ ...form, part_id: "" })} data-testid="task-part-unlock">change</button>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            <Select
                                                value={form._dlg_customer || "__all__"}
                                                onValueChange={(v) => setForm({ ...form, _dlg_customer: v, _picked_part_ids: [] })}
                                            >
                                                <SelectTrigger className="rounded-none h-9" data-testid="dlg-customer-select"><SelectValue placeholder="Pick customer" /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="__all__">— All customers —</SelectItem>
                                                    {Array.from(new Set(parts.filter((p) => !p.is_child && p.customer_name).map((p) => p.customer_name))).sort().map((c) => (
                                                        <SelectItem key={c} value={c}>{c}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <div className="border border-zinc-200 max-h-40 overflow-y-auto" data-testid="dlg-parts-list">
                                                {parts
                                                    .filter((p) => !p.is_child)
                                                    .filter((p) => !form._dlg_customer || form._dlg_customer === "__all__" || (p.customer_name || "") === form._dlg_customer)
                                                    .map((p) => {
                                                        const picked = (form._picked_part_ids || []).includes(p.part_id);
                                                        return (
                                                            <label key={p.part_id} className="flex items-center gap-2 px-2 py-1.5 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 cursor-pointer text-xs">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={picked}
                                                                    onChange={(e) => {
                                                                        const cur = form._picked_part_ids || [];
                                                                        const next = e.target.checked ? [...cur, p.part_id] : cur.filter((id) => id !== p.part_id);
                                                                        setForm({ ...form, _picked_part_ids: next });
                                                                    }}
                                                                    data-testid={`dlg-part-${p.part_id}`}
                                                                />
                                                                <span className="font-medium">{p.part_name}</span>
                                                                {p.part_number && <span className="text-zinc-400 font-mono">({p.part_number})</span>}
                                                                {p.customer_name && <span className="text-zinc-400 ml-auto">{p.customer_name}</span>}
                                                            </label>
                                                        );
                                                    })}
                                                {parts.filter((p) => !p.is_child && (!form._dlg_customer || form._dlg_customer === "__all__" || (p.customer_name || "") === form._dlg_customer)).length === 0 && (
                                                    <div className="text-xs text-zinc-400 italic px-2 py-3">No parts for this customer.</div>
                                                )}
                                            </div>
                                            <div className="text-[10px] text-zinc-500 font-mono" data-testid="dlg-parts-picked-count">
                                                {(form._picked_part_ids || []).length === 0 ? "Tick one or more parts. One task will be created per ticked part." : `${(form._picked_part_ids || []).length} part(s) selected — same task will be created for each.`}
                                            </div>
                                        </div>
                                    )}
                                </Field>
                                <Field label="Parent Task (for sub-tasks)">
                                    <Select value={form.parent_task_id || "__none__"} onValueChange={(v) => setForm({ ...form, parent_task_id: v === "__none__" ? "" : v })}>
                                        <SelectTrigger className="rounded-none" data-testid="task-parent-select"><SelectValue placeholder="None" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="__none__">— None (top-level) —</SelectItem>
                                            {tasks.filter((t) => t.part_id === form.part_id && t.task_id !== editingId && !t.parent_task_id).map((t) => (
                                                <SelectItem key={t.task_id} value={t.task_id}>{t.title}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </Field>
                                <Field label="Assigned To">
                                    <Select value={form.assigned_to || "__none__"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "__none__" ? "" : v })}>
                                        <SelectTrigger className="rounded-none" data-testid="task-assignee-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="__none__">— Unassigned —</SelectItem>
                                            {users.map((u) => (
                                                <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </Field>
                                <Field label="Depends on (waits for)">
                                    <Select
                                        value={form.depends_on?.[0] || "__none__"}
                                        onValueChange={(v) => setForm({ ...form, depends_on: v === "__none__" ? [] : [v] })}
                                    >
                                        <SelectTrigger className="rounded-none" data-testid="task-depends-select"><SelectValue placeholder="No dependency" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="__none__">— None —</SelectItem>
                                            {tasks.filter((t) => t.part_id === form.part_id && t.task_id !== editingId).map((t) => (
                                                <SelectItem key={t.task_id} value={t.task_id}>{t.title}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </Field>
                                <Field label="Planned Start">
                                    <Input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value, _dates_dirty: true })} data-testid="task-start-input" />
                                </Field>
                                <Field label="Planned Due">
                                    <Input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value, _dates_dirty: true })} data-testid="task-due-input" />
                                </Field>
                                <Field label="Actual Start">
                                    <Input type="date" value={form.actual_start || ""} onChange={(e) => setForm({ ...form, actual_start: e.target.value })} data-testid="task-actual-start-input" />
                                </Field>
                                <Field label="Actual End">
                                    <Input type="date" value={form.actual_end || ""} onChange={(e) => setForm({ ...form, actual_end: e.target.value })} data-testid="task-actual-end-input" />
                                </Field>
                                <Field label="Priority">
                                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                                        <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="low">Low</SelectItem>
                                            <SelectItem value="medium">Medium</SelectItem>
                                            <SelectItem value="high">High</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </Field>
                                <Field label="Status">
                                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                                        <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {STATUS_OPTS.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </Field>
                            </div>
                            {editingId && form._dates_dirty && (
                                <div className="mt-3 border-l-4 border-[#FFB703] bg-[#FFFBEB] px-3 py-2">
                                    <Label className="text-[10px] uppercase tracking-wider text-zinc-600">Reschedule reason</Label>
                                    <Input
                                        value={form.reschedule_reason || ""}
                                        onChange={(e) => setForm({ ...form, reschedule_reason: e.target.value })}
                                        placeholder="Why was this task rescheduled? e.g. material delay, RFQ change"
                                        className="rounded-none mt-1"
                                        data-testid="task-reschedule-reason"
                                    />
                                </div>
                            )}
                            </div>
                            <DialogFooter className="px-6 pb-6 pt-3 border-t border-zinc-200 shrink-0 bg-white">
                                <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
                                <Button className="rounded-none bg-[#002FA7] hover:bg-[#00227B]" onClick={save} data-testid="task-save-btn">Save</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-2 flex-nowrap overflow-x-auto">
                <Label className="text-[11px] uppercase tracking-wider text-zinc-500 shrink-0">Customer</Label>
                <Select value={filterCustomer} onValueChange={setFilterCustomer}>
                    <SelectTrigger className="rounded-none h-8 w-44 shrink-0" data-testid="filter-customer"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All customers</SelectItem>
                        {Array.from(new Set(parts.filter((p) => !p.is_child && p.customer_name).map((p) => p.customer_name))).sort().map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Label className="text-[11px] uppercase tracking-wider text-zinc-500 ml-2 shrink-0">Part</Label>
                <Select value={filterPart} onValueChange={setFilterPart}>
                    <SelectTrigger className="rounded-none h-8 w-56 shrink-0" data-testid="filter-part"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All parts</SelectItem>
                        {parts.filter((p) => !p.is_child && (filterCustomer === "all" || (p.customer_name || "") === filterCustomer)).map((p) => (
                            <SelectItem key={p.part_id} value={p.part_id}>{p.part_name} {p.part_number ? `(${p.part_number})` : ""}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Label className="text-[11px] uppercase tracking-wider text-zinc-500 ml-2 shrink-0">Assignee</Label>
                <Select value={filterUser} onValueChange={setFilterUser}>
                    <SelectTrigger className="rounded-none h-8 w-40 shrink-0"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Label className="text-[11px] uppercase tracking-wider text-zinc-500 ml-2 shrink-0">Status</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="rounded-none h-8 w-32 shrink-0"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        {STATUS_OPTS.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>

            {/* Views */}
            <Tabs value={view} onValueChange={setView} data-testid="tasks-view-tabs">
                <TabsList className="rounded-none">
                    <TabsTrigger value="list" className="rounded-none" data-testid="view-list">List</TabsTrigger>
                    <TabsTrigger value="calendar" className="rounded-none" data-testid="view-calendar">Calendar</TabsTrigger>
                    <TabsTrigger value="gantt" className="rounded-none" data-testid="view-gantt">Gantt</TabsTrigger>
                    <TabsTrigger value="load" className="rounded-none" data-testid="view-load">Member Load</TabsTrigger>
                </TabsList>

                <TabsContent value="list" className="pt-4">
                    <div className="flex justify-end mb-2">
                        <Button
                            variant="outline"
                            className="rounded-none h-8 text-xs"
                            onClick={() => {
                                // CSV export of the currently filtered tasks
                                const rows = filtered.map((t) => {
                                    const p = partById[t.part_id] || {};
                                    const u = t.assigned_to ? userById[t.assigned_to] : null;
                                    return {
                                        title: t.title,
                                        customer: p.customer_name || "",
                                        part: p.part_name || "",
                                        part_number: p.part_number || "",
                                        status: t.status,
                                        priority: t.priority,
                                        assignee: u ? (u.name || u.email) : "",
                                        start_date: t.start_date || "",
                                        due_date: t.due_date || "",
                                        actual_start: t.actual_start || "",
                                        actual_end: t.actual_end || "",
                                        original_due_date: t.original_due_date || "",
                                        reschedule_count: t.reschedule_count || 0,
                                    };
                                });
                                const headers = Object.keys(rows[0] || { title: "" });
                                const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
                                const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))].join("\n");
                                const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
                                const a = document.createElement("a");
                                a.href = URL.createObjectURL(blob);
                                a.download = `tasks_${new Date().toISOString().slice(0, 10)}.csv`;
                                a.click();
                                URL.revokeObjectURL(a.href);
                            }}
                            data-testid="tasks-export-csv"
                        >
                            Download CSV ({filtered.length})
                        </Button>
                    </div>
                    <ListView tasks={filtered} partById={partById} userById={userById} isOverdue={isOverdue} onEdit={startEdit} onDelete={remove} onStatus={setStatus} isExecutor={isExecutor} currentUser={user} onComment={addComment} onUploadPhoto={uploadCompletionPhoto} />
                </TabsContent>
                <TabsContent value="calendar" className="pt-4">
                    <CalendarView tasks={filtered} partById={partById} isOverdue={isOverdue} onEdit={startEdit} />
                </TabsContent>
                <TabsContent value="gantt" className="pt-4">
                    <GanttView tasks={filtered} partById={partById} userById={userById} isOverdue={isOverdue} onEdit={startEdit} onUpdateDates={updateDates} onUpdateTitle={async (t, title) => { try { await api.put(`/tasks/${t.task_id}`, { ...t, title, reschedule_reason: "" }); toast.success("Title updated"); await load(); } catch { toast.error("Update failed"); } }} isExecutor={isExecutor} onReorderRows={reorderRows} onAddTaskForPart={(partId) => { setEditingId(null); setForm({ ...emptyTask, part_id: partId }); setOpen(true); }} />
                </TabsContent>
                <TabsContent value="load" className="pt-4">
                    <LoadView loadByUser={loadByUser} userById={userById} />
                    <RewardLeaderboard isAdmin={user?.role === "admin"} />
                </TabsContent>
            </Tabs>
        </div>
    );
}

function Field({ label, full, children }) {
    return (
        <div className={full ? "col-span-2" : ""}>
            <Label className="text-[11px] uppercase tracking-wider text-zinc-500 font-mono">{label}</Label>
            <div className="mt-1">{children}</div>
        </div>
    );
}

function PriorityDot({ p }) {
    const c = PRIORITY.find((x) => x.id === p)?.color || "#94a3b8";
    return <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: c }} title={p} />;
}

function StatusBadge({ s, onChange }) {
    const opt = STATUS_OPTS.find((x) => x.id === s) || STATUS_OPTS[0];
    return (
        <Select value={s} onValueChange={onChange}>
            <SelectTrigger className="rounded-none h-7 text-xs font-mono px-2" style={{ background: opt.bg, color: opt.color, borderColor: opt.color }}>
                <SelectValue />
            </SelectTrigger>
            <SelectContent>{STATUS_OPTS.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
    );
}

function ListView({ tasks, partById, userById, isOverdue, onEdit, onDelete, onStatus, isExecutor, currentUser, onComment, onUploadPhoto }) {
    const [expanded, setExpanded] = React.useState(null); // task_id whose comments are visible
    if (!tasks.length) return <div className="text-zinc-400 italic py-12 text-center">No tasks. Create one with the New Task button.</div>;
    // Order: parent rows first, with subtasks nested directly below
    const taskById = Object.fromEntries(tasks.map((t) => [t.task_id, t]));
    const ordered = [];
    const pushed = new Set();
    tasks.filter((t) => !t.parent_task_id).forEach((p) => {
        ordered.push({ ...p, _indent: 0 }); pushed.add(p.task_id);
        tasks.filter((c) => c.parent_task_id === p.task_id).forEach((c) => {
            ordered.push({ ...c, _indent: 1 }); pushed.add(c.task_id);
        });
    });
    // orphans (parent missing in current filter)
    tasks.forEach((t) => { if (!pushed.has(t.task_id)) ordered.push({ ...t, _indent: 0 }); });
    return (
        <table className="w-full sheet-table" data-testid="tasks-list-table">
            <thead><tr><th></th><th>Title</th><th>Part</th><th>Assignee</th><th>Planned</th><th>Actual</th><th>Delay</th><th>Status</th><th></th></tr></thead>
            <tbody>
                {ordered.map((t) => {
                    const delay = (() => {
                        if (t.status !== "done") {
                            if (t.due_date && t.due_date < new Date().toISOString().slice(0, 10)) {
                                const days = Math.round((new Date() - new Date(t.due_date)) / 86400000);
                                return { days, sign: "+" };
                            }
                            return null;
                        }
                        // done — compare actual_end vs due_date
                        if (t.actual_end && t.due_date) {
                            const days = Math.round((new Date(t.actual_end) - new Date(t.due_date)) / 86400000);
                            return { days, sign: days > 0 ? "+" : (days < 0 ? "" : "") };
                        }
                        return null;
                    })();
                    return (
                        <tr key={t.task_id} className={isOverdue(t) ? "bg-[#FEF2F2]" : ""}>
                            <td><PriorityDot p={t.priority} /></td>
                            <td className="font-medium" style={{ paddingLeft: 12 + (t._indent || 0) * 20 }}>
                                {t._indent ? <span className="text-zinc-300 mr-1">↳</span> : null}
                                {t.title}
                                {isOverdue(t) && <span className="ml-2 text-[10px] uppercase text-[#E63946] font-mono">overdue</span>}
                                {t.depends_on?.length > 0 && <span className="ml-2 text-[10px] text-zinc-400" title="Depends on">→ {taskById[t.depends_on[0]]?.title?.slice(0, 16) || "…"}</span>}
                            </td>
                            <td className="text-xs">{t.part_id ? (partById[t.part_id]?.part_name || t.part_id) : <span className="text-zinc-400">—</span>}</td>
                            <td className="text-xs">{t.assigned_to ? (userById[t.assigned_to]?.name || userById[t.assigned_to]?.email || t.assigned_to) : <span className="text-zinc-400">—</span>}</td>
                            <td className="font-mono text-[11px]">
                                {t.start_date || "—"} → {t.due_date || "—"}
                                {(t.reschedule_count || 0) > 0 && (() => {
                                    const last = (t.reschedule_history || []).slice(-1)[0] || {};
                                    const reason = last.reason || "";
                                    return (
                                        <div className="text-[9px] text-[#E63946] mt-0.5" title={`Originally planned: ${t.original_start_date || "—"} → ${t.original_due_date || "—"}${reason ? `\nReason: ${reason}` : ""}`} data-testid={`reschedule-badge-${t.task_id}`}>
                                            ⟲ Rescheduled {t.reschedule_count}× · was {t.original_start_date || "—"} → {t.original_due_date || "—"}
                                            {reason && <div className="text-[9px] text-zinc-500 italic">↳ {reason}</div>}
                                        </div>
                                    );
                                })()}
                            </td>
                            <td className="font-mono text-[11px]">{t.actual_start || "—"} → {t.actual_end || "—"}</td>
                            <td className={`font-mono text-xs ${delay && delay.days > 0 ? "text-[#E63946] font-semibold" : (delay && delay.days < 0 ? "text-[#2A9D8F]" : "")}`}>
                                {delay ? `${delay.sign}${delay.days}d` : "—"}
                            </td>
                            <td><StatusBadge s={t.status} onChange={(v) => onStatus(t, v)} /></td>
                            <td className="text-right w-32">
                                {isExecutor ? (
                                    <ExecutorActions t={t} currentUser={currentUser} onStatus={onStatus} onComment={onComment} onUploadPhoto={onUploadPhoto} expanded={expanded === t.task_id} setExpanded={(v) => setExpanded(v ? t.task_id : null)} />
                                ) : (
                                    <>
                                        <button onClick={() => onEdit(t)} className="text-zinc-400 hover:text-[#002FA7] mr-2"><Pencil size={13} /></button>
                                        <button onClick={() => onDelete(t.task_id)} className="text-zinc-400 hover:text-[#E63946]"><Trash2 size={13} /></button>
                                    </>
                                )}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

function CalendarView({ tasks, partById, isOverdue, onEdit }) {
    const [cursor, setCursor] = useState(new Date());
    const [openDate, setOpenDate] = useState(null);  // iso string of clicked date
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const startWeekday = first.getDay();
    const days = [];
    for (let i = 0; i < startWeekday; i++) days.push(null);
    for (let d = 1; d <= last.getDate(); d++) days.push(new Date(year, month, d));
    const byDate = useMemo(() => {
        const m = {};
        tasks.forEach((t) => {
            if (!t.due_date) return;
            if (!m[t.due_date]) m[t.due_date] = [];
            m[t.due_date].push(t);
        });
        return m;
    }, [tasks]);
    const monthLabel = cursor.toLocaleString("en-US", { month: "long", year: "numeric" });
    const openTasks = openDate ? (byDate[openDate] || []) : [];
    return (
        <div data-testid="tasks-calendar-view">
            <div className="flex items-center justify-between mb-3">
                <Button variant="outline" className="rounded-none h-8" onClick={() => setCursor(new Date(year, month - 1, 1))}>← Prev</Button>
                <h2 className="font-mono uppercase tracking-wider text-zinc-700">{monthLabel}</h2>
                <Button variant="outline" className="rounded-none h-8" onClick={() => setCursor(new Date(year, month + 1, 1))}>Next →</Button>
            </div>
            <div className="grid grid-cols-7 gap-px bg-zinc-200 border border-zinc-200">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                    <div key={d} className="bg-zinc-50 py-2 text-center text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{d}</div>
                ))}
                {days.map((d, i) => {
                    if (!d) return <div key={i} className="bg-white min-h-[90px]"></div>;
                    const iso = d.toISOString().slice(0, 10);
                    const cellTasks = byDate[iso] || [];
                    return (
                        <div
                            key={i}
                            className={`bg-white min-h-[90px] p-1.5 overflow-hidden ${cellTasks.length ? "cursor-pointer hover:bg-zinc-50" : ""}`}
                            onClick={() => cellTasks.length && setOpenDate(iso)}
                            data-testid={`cal-cell-${iso}`}
                        >
                            <div className="text-xs font-mono text-zinc-500 mb-1">{d.getDate()}</div>
                            {cellTasks.slice(0, 3).map((t) => (
                                <div
                                    key={t.task_id}
                                    onClick={(e) => { e.stopPropagation(); onEdit(t); }}
                                    className="text-[10px] px-1 py-0.5 mb-0.5 truncate cursor-pointer border-l-2"
                                    style={{ background: isOverdue(t) ? "#FEE2E2" : STATUS_OPTS.find((s) => s.id === t.status)?.bg, borderColor: STATUS_OPTS.find((s) => s.id === t.status)?.color }}
                                    title={t.title}
                                >
                                    {t.title}
                                </div>
                            ))}
                            {cellTasks.length > 3 && (
                                <div className="text-[9px] text-[#002FA7] underline" data-testid={`cal-more-${iso}`}>+{cellTasks.length - 3} more · view all</div>
                            )}
                        </div>
                    );
                })}
            </div>
            <Dialog open={!!openDate} onOpenChange={(v) => !v && setOpenDate(null)}>
                <DialogContent className="rounded-none max-w-lg max-h-[80vh] flex flex-col overflow-hidden">
                    <DialogHeader>
                        <DialogTitle data-testid="cal-detail-title">Tasks on {openDate}</DialogTitle>
                    </DialogHeader>
                    <div className="overflow-y-auto flex-1 divide-y divide-zinc-200" data-testid="cal-detail-list">
                        {openTasks.length === 0 && <div className="text-xs text-zinc-400 italic py-4 text-center">No tasks on this day.</div>}
                        {openTasks.map((t) => (
                            <button
                                key={t.task_id}
                                onClick={() => { setOpenDate(null); onEdit(t); }}
                                className="w-full text-left py-2 px-1 hover:bg-zinc-50 flex items-center justify-between gap-3"
                                data-testid={`cal-detail-task-${t.task_id}`}
                            >
                                <div className="min-w-0">
                                    <div className="text-sm font-medium truncate">{t.title}</div>
                                    <div className="text-[10px] text-zinc-500 font-mono">{partById[t.part_id]?.part_name || "—"} · {t.status}{isOverdue(t) ? " · OVERDUE" : ""}</div>
                                </div>
                                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 border" style={{ background: STATUS_OPTS.find((s) => s.id === t.status)?.bg, borderColor: STATUS_OPTS.find((s) => s.id === t.status)?.color }}>{t.status}</span>
                            </button>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}


function RewardLeaderboard({ isAdmin }) {
    const [cfg, setCfg] = React.useState(null);
    const [board, setBoard] = React.useState([]);
    const [saving, setSaving] = React.useState(false);
    const load = React.useCallback(async () => {
        try {
            const r = await api.get(`/rewards/leaderboard`);
            const data = r.data.leaderboard || [];
            // Sort alphabetically by name so no implicit "leader" by position
            data.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
            setCfg(r.data.config); setBoard(data);
        } catch { /* silent */ }
    }, []);
    React.useEffect(() => { load(); }, [load]);
    if (!cfg) return null;
    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/rewards/config`, {
                points_before_time: Number(cfg.points_before_time) || 0,
                points_on_time: Number(cfg.points_on_time) || 0,
                points_overdue: Number(cfg.points_overdue) || 0,
                points_per_reschedule: Number(cfg.points_per_reschedule) || 0,
            });
            toast.success("Reward points updated"); await load();
        } catch { toast.error("Save failed"); }
        finally { setSaving(false); }
    };
    return (
        <div className="mt-8 border border-zinc-200" data-testid="reward-leaderboard-section">
            <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Rewards · Member Points</span>
                </div>
                <div className="flex items-center gap-3 text-[10px] font-mono text-zinc-500">
                    <span title="Done before due_date">Before: <b className="text-[#2A9D8F]">+{cfg.points_before_time}</b></span>
                    <span title="Done on due_date">On-time: <b>+{cfg.points_on_time}</b></span>
                    <span title="Done after due_date or still open past due">Overdue: <b className="text-zinc-700">{cfg.points_overdue}</b></span>
                    <span title="Each time start/due date is edited">Reschedule: <b>{cfg.points_per_reschedule}</b></span>
                </div>
            </div>
            <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-[11px] text-zinc-700">
                <b>How it works:</b> points accumulate per task per user — they can be encashed monthly. No &ldquo;leader&rdquo; ranking is shown so users with fewer assigned tasks aren&rsquo;t disadvantaged. {!isAdmin && <span className="italic">Admins can change the point values.</span>}
            </div>
            {isAdmin && (
                <div className="px-4 py-2 border-b border-zinc-200 flex items-center gap-3 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Edit points:</span>
                    {[
                        { k: "points_before_time", label: "Before time" },
                        { k: "points_on_time", label: "On time" },
                        { k: "points_overdue", label: "Overdue" },
                        { k: "points_per_reschedule", label: "Per reschedule" },
                    ].map((f) => (
                        <label key={f.k} className="flex items-center gap-1 text-xs">
                            <span className="text-zinc-500">{f.label}</span>
                            <Input type="number" value={cfg[f.k] ?? 0} onChange={(e) => setCfg({ ...cfg, [f.k]: e.target.value })} className="rounded-none h-7 w-16 font-mono" data-testid={`reward-cfg-${f.k}`} />
                        </label>
                    ))}
                    <Button className="rounded-none h-7 text-xs bg-[#002FA7] hover:bg-[#00227B]" disabled={saving} onClick={save} data-testid="reward-save-btn">{saving ? "Saving…" : "Save"}</Button>
                </div>
            )}
            {board.length === 0 ? (
                <div className="text-xs text-zinc-400 italic p-6 text-center">No tasks assigned to any user yet — once tasks are completed (or go overdue) points show up here.</div>
            ) : (
                <table className="w-full sheet-table" data-testid="reward-leaderboard-table">
                    <thead>
                        <tr><th className="w-12">#</th><th>User</th><th className="text-right">Before time</th><th className="text-right">On time</th><th className="text-right">Overdue</th><th className="text-right">Reschedules</th><th className="text-right font-bold">Total points</th></tr>
                    </thead>
                    <tbody>
                        {board.map((r, i) => (
                            <tr key={r.user_id} data-testid={`reward-row-${r.user_id}`}>
                                <td className="font-mono text-zinc-400">{i + 1}</td>
                                <td className="font-medium">{r.name}</td>
                                <td className="font-mono text-right text-[#2A9D8F]">{r.before_time}</td>
                                <td className="font-mono text-right">{r.on_time}</td>
                                <td className="font-mono text-right text-zinc-500">{r.overdue}</td>
                                <td className="font-mono text-right text-zinc-500">{r.reschedules}</td>
                                <td className={`font-mono text-right font-bold ${r.points >= 0 ? "text-[#002FA7]" : "text-zinc-500"}`}>{r.points}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}


function LoadView({ loadByUser, userById }) {
    const entries = Object.entries(loadByUser);
    if (!entries.length) return <div className="text-zinc-400 italic py-12 text-center">No open tasks.</div>;
    const max = Math.max(...entries.map(([, v]) => (v.open || 0) + (v.in_progress || 0)));
    return (
        <div className="space-y-2" data-testid="tasks-load-view">
            {entries.sort((a, b) => ((b[1].open || 0) + (b[1].in_progress || 0)) - ((a[1].open || 0) + (a[1].in_progress || 0))).map(([uid, v]) => {
                const total = (v.open || 0) + (v.in_progress || 0);
                const name = uid === "unassigned" ? "Unassigned" : (userById[uid]?.name || userById[uid]?.email || uid);
                return (
                    <div key={uid} className="border border-zinc-200 p-3 flex items-center gap-3">
                        <div className="w-48 truncate font-medium">{name}</div>
                        <div className="flex-1">
                            <div className="h-6 bg-zinc-100 relative">
                                <div className="absolute top-0 left-0 h-full" style={{ width: `${max ? (v.open / max) * 100 : 0}%`, background: "#FFB703" }} title={`Open: ${v.open}`} />
                                <div className="absolute top-0 h-full" style={{ left: `${max ? (v.open / max) * 100 : 0}%`, width: `${max ? ((v.in_progress || 0) / max) * 100 : 0}%`, background: "#002FA7" }} title={`In progress: ${v.in_progress || 0}`} />
                            </div>
                        </div>
                        <div className="w-40 text-right text-xs font-mono">
                            <span style={{ color: "#FFB703" }}>{v.open || 0} open</span>
                            <span className="mx-1">·</span>
                            <span style={{ color: "#002FA7" }}>{v.in_progress || 0} doing</span>
                            {v.overdue > 0 && <><span className="mx-1">·</span><span className="text-[#E63946] font-semibold">{v.overdue} overdue</span></>}
                            <span className="ml-2 text-zinc-400">({total})</span>
                        </div>
                    </div>
                );
            })}
            <div className="text-[10px] text-zinc-500 font-mono mt-2 inline-flex items-center gap-4">
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3" style={{ background: "#FFB703" }} /> Open</span>
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3" style={{ background: "#002FA7" }} /> In progress</span>
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3" style={{ background: "#E63946" }} /> Overdue marker</span>
            </div>
        </div>
    );
}

function ExecutorActions({ t, currentUser, onStatus, onComment, onUploadPhoto, expanded, setExpanded }) {
    const isMine = t.assigned_to === currentUser?.user_id;
    const fileRef = React.useRef(null);
    const [commentText, setCommentText] = React.useState("");
    if (!isMine) {
        // View-only — just allow comments
        return (
            <button
                onClick={() => setExpanded(!expanded)}
                className="text-xs text-[#002FA7] hover:underline font-mono"
                data-testid={`exec-view-${t.task_id}`}
            >{expanded ? "Close" : "Comments"} ({t.comments?.length || 0})</button>
        );
    }
    return (
        <div className="inline-flex flex-col items-end gap-1">
            {t.status === "open" && (
                <button onClick={() => onStatus(t, "in_progress")} data-testid={`exec-start-${t.task_id}`}
                    className="px-2 py-0.5 text-[10px] uppercase tracking-wider font-mono bg-[#002FA7] text-white hover:bg-[#00227B]">Start</button>
            )}
            {t.status === "in_progress" && (
                <>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onUploadPhoto(t, f); e.target.value = ""; }} />
                    <button onClick={() => fileRef.current?.click()} data-testid={`exec-done-photo-${t.task_id}`}
                        className="px-2 py-0.5 text-[10px] uppercase tracking-wider font-mono bg-[#2A9D8F] text-white hover:bg-[#218377]">Done + Photo</button>
                    <button onClick={() => onStatus(t, "done")} data-testid={`exec-done-${t.task_id}`}
                        className="px-2 py-0.5 text-[10px] uppercase tracking-wider font-mono border border-[#2A9D8F] text-[#2A9D8F] hover:bg-[#E0F4F1]">Done (no photo)</button>
                </>
            )}
            {t.status === "done" && t.completion_photo_path && (
                <a href={`${API}/files/${t.completion_photo_path}`} target="_blank" rel="noreferrer" className="text-[10px] text-[#2A9D8F] underline" data-testid={`exec-photo-link-${t.task_id}`}>View proof</a>
            )}
            <button onClick={() => setExpanded(!expanded)} className="text-[10px] text-[#002FA7] underline" data-testid={`exec-comments-${t.task_id}`}>
                Comments ({t.comments?.length || 0})
            </button>
            {expanded && (
                <div className="mt-1 w-56 border border-zinc-200 bg-white p-2 text-left">
                    <div className="max-h-40 overflow-y-auto space-y-1 mb-2">
                        {(t.comments || []).map((c, i) => (
                            <div key={i} className="text-[10px] font-mono">
                                <div className="text-zinc-500">{c.user_name} · {(c.created_at || "").slice(0, 16).replace("T", " ")}</div>
                                <div>{c.text}</div>
                            </div>
                        ))}
                        {(t.comments || []).length === 0 && <div className="text-[10px] text-zinc-400 italic">No comments yet.</div>}
                    </div>
                    <textarea value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder="Add a comment…" className="w-full h-12 text-[11px] border border-zinc-200 p-1" data-testid={`exec-comment-input-${t.task_id}`} />
                    <button
                        onClick={async () => { if (commentText.trim()) { await onComment(t, commentText.trim()); setCommentText(""); } }}
                        className="mt-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-mono bg-[#002FA7] text-white"
                        data-testid={`exec-comment-submit-${t.task_id}`}
                    >Post</button>
                </div>
            )}
        </div>
    );
}

